import {Edit} from "@mui/icons-material";
import React, {useState} from "react";
import {useCurrentUser} from "../../../../common/hooks/useUser";
import Grid from "../../../../components/dialogs/Grid";
import TextField from "../../../../components/fields/textfield/TextField";
import CircularProgress from "../../../../components/progress/CircularProgress";
import SectionActionBar from "../../../../components/section/SectionActionBar";
import {SectionActionButton} from "../../../../components/section/SectionActionButton";
import SectionHeader from "../../../../components/section/SectionHeader";
import {t} from "../../../../store/localization/TranslMessasge";
import {CommonTranslationKey} from "../CommonTranslationKey";
import {useUserByLoginQuery} from "./UserQuery";
import {UserRoleTranslator} from "./role/UserRoleTranslator";
import {UsersTranslationKey} from "./UsersTranslationKey";
import {UserValidator} from "./UserValidation";
import {UserRoleProvider} from "./permission/UserRoleProvider";
import UserBasicInfoUpdateDialog from "./UserBasicInfoUpdateDialog";
import UserRoleUpdateDialog from "./role/UserRoleUpdateDialog";

interface UserBasicInfoPanelProps {
    login: string;
    onUpdate?: () => void;
    userValidator: UserValidator;
}

export default function UserBasicInfoPanel({login, onUpdate, userValidator}: Readonly<UserBasicInfoPanelProps>) {
    const [basicInfoDialogOpen, setBasicInfoDialogOpen] = useState(false);
    const [roleDialogOpen, setRoleDialogOpen] = useState(false);

    const loggedUser = useCurrentUser();

    const {data: user, refetch, isLoading} = useUserByLoginQuery(login);

    function onUserUpdated() {
        refetch();
        onUpdate?.();
    }

    function formatUserRole(role: string): string {
        return UserRoleTranslator.get().translateUserRole(role) ?? "";
    }

    return (
            <>
                <Grid container>
                    {user &&
                        <>
                            <Grid item xs={12}>
                                <SectionHeader title={t(UsersTranslationKey.DETAILS)}>
                                    <SectionActionBar>
                                        <SectionActionButton
                                                icon={<Edit fontSize="inherit"/>}
                                                tooltip={t(CommonTranslationKey.EDIT)}
                                                onClick={() => setBasicInfoDialogOpen(true)}
                                                visible={user.userAcl.canUpdate}/>
                                    </SectionActionBar>
                                </SectionHeader>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                        id="user-detail-basic-panel-firstname"
                                        label={t(UsersTranslationKey.USER_FIRST_NAME)}
                                        value={user.firstName || ""}
                                        readOnly={true}/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                        id="user-detail-basic-panel-lastname"
                                        label={t(UsersTranslationKey.USER_LAST_NAME)}
                                        value={user.lastName || ""}
                                        readOnly={true}/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                        id="user-detail-basic-panel-login"
                                        label={t(UsersTranslationKey.USER_LOGIN)}
                                        value={user.login}
                                        readOnly={true}/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                        id="user-detail-basic-panel-active"
                                        label={t(UsersTranslationKey.USER_STATE)}
                                        value={user.userActive ? t(UsersTranslationKey.USER_IS_ACTIVE) : t(UsersTranslationKey.USER_IS_INACTIVE)}
                                        readOnly={true}/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                        id="user-detail-basic-panel-email"
                                        label={t(UsersTranslationKey.USER_EMAIL)}
                                        value={user.email || ""}
                                        readOnly={true}/>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                        id="user-detail-basic-panel-phone"
                                        label={t(UsersTranslationKey.USER_PHONE)}
                                        value={user.phone || ""}
                                        readOnly={true}/>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                        id="user-detail-basic-panel-description"
                                        label={t(UsersTranslationKey.NOTE)}
                                        value={user.description || ""}
                                        readOnly={true}
                                        multiline
                                        rows={4}/>
                            </Grid>

                            <Grid item xs={12}>
                                <SectionHeader title={t(UsersTranslationKey.USER_ROLE)}>
                                    <SectionActionBar>
                                        <SectionActionButton
                                                icon={<Edit fontSize="inherit"/>}
                                                tooltip={t(CommonTranslationKey.EDIT)}
                                                onClick={() => setRoleDialogOpen(true)}
                                                visible={user.userAcl.canUpdateRole}/>
                                    </SectionActionBar>
                                </SectionHeader>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                        id="user-detail-basic-panel-role"
                                        label={t(UsersTranslationKey.USER_ROLE)}
                                        value={formatUserRole(user.role)}
                                        readOnly={true}/>
                            </Grid>
                        </>
                    }

                    {isLoading &&
                        <Grid item xs={12}>
                            <CircularProgress variant={"indeterminate"}/>
                        </Grid>
                    }
                </Grid>

                {user &&
                        <>
                            <UserBasicInfoUpdateDialog
                                    open={basicInfoDialogOpen}
                                    user={user}
                                    userValidator={userValidator}
                                    onClose={() => setBasicInfoDialogOpen(false)}
                                    onUpdated={onUserUpdated}/>

                            <UserRoleUpdateDialog
                                    open={roleDialogOpen}
                                    user={user}
                                    availableRoles={UserRoleProvider.getAssignableRolesForUser(loggedUser, user.role, user.login)}
                                    onClose={() => setRoleDialogOpen(false)}
                                    onUpdated={onUserUpdated}/>
                        </>
                }
            </>
    );
}
